import { MIMETYPE_JSON } from '@disy/lib/media-type';

import type { ObjectPrivilege } from 'cadenza/access-management/model/object-privilege.gen';
import { ajax, encodePathVariable } from 'cadenza/api-client/api';
import type {
  AssociationCandidatesDto,
  AssociationDto,
  AssociationsDto,
  MutableAssociationDto,
} from 'cadenza/api-client/dto/associations-dto';
import type { AttributeColorRulesListDto } from 'cadenza/api-client/dto/attribute-color-rules-list-dto';
import type { AttributeDto } from 'cadenza/api-client/dto/attribute-dto';
import type { AttributeValueColorRuleDtoWithOptionalColor } from 'cadenza/api-client/dto/attribute-value-color-rule-dto';
import type { AttributeValueRowDto } from 'cadenza/api-client/dto/attribute-value-row-dto';
import type { ChangesBetweenSourceAndCopiedObjecttypeDto } from 'cadenza/api-client/dto/changes-between-source-and-copied-objecttype-dto';
import type { CopyObjecttypeDto, ObjecttypeCopyContextDto } from 'cadenza/api-client/dto/copy-objecttype-dto';
import type { CreateBasicObjecttypeRequestDto } from 'cadenza/api-client/dto/create-basic-objecttype-request-dto';
import type { CreateCombinedObjecttypeRequestDto } from 'cadenza/api-client/dto/create-combined-objecttype-request-dto';
import type { DatatableColumnDto } from 'cadenza/api-client/dto/datatable-column-dto';
import type { DateHierarchyDto } from 'cadenza/api-client/dto/date-hierarchy-dto';
import type { FunctionFormulaStatusDto } from 'cadenza/api-client/dto/function-formula-status-dto';
import type { MultiAttributeHierarchyDto } from 'cadenza/api-client/dto/multi-attribute-hierarchy-dto';
import type { MutableObjecttypeDto, ObjecttypeDto, ObjecttypeListingDto } from 'cadenza/api-client/dto/objecttype-dto';
import type { ObjecttypeMemberDto } from 'cadenza/api-client/dto/objecttype-member-dto';
import type { ObjecttypePathDto, ObjecttypePathElementDto } from 'cadenza/api-client/dto/objecttype-path-dto';
import type { ObjecttypeSelectedMembersDto } from 'cadenza/api-client/dto/objecttype-selected-members-dto';
import type { ObjecttypeUsagesDto } from 'cadenza/api-client/dto/objecttype-usages-dto';
import type { UpdateObjecttypeDto } from 'cadenza/api-client/dto/update-objecttype-dto';
import type { ObjecttypeDataScope } from 'cadenza/api-client/model/objecttype-data-scope.gen';
import type { OverrideableObjecttypeMemberProperty } from 'cadenza/api-client/model/overrideable-objecttype-member-property.gen';
import { type AttributeFilter, createPatch } from 'cadenza/api-client/repositories-api';
import { type TableData, type TableParams, tableParams } from 'cadenza/api-client/tables-api';
import type { WorkOrderId } from 'cadenza/api-client/workorder-api';
import type { CreateAttributeRequestDto } from 'cadenza/objecttype/objecttype-details/model/create-attribute-request-dto';
import type { ObjecttypeId, ObjecttypeIdString } from 'cadenza/repository/model/objecttype-id.gen';
import type { RepositoryName } from 'cadenza/repository/model/repository-name.gen';
import type { WorkbookId } from 'cadenza/repository/model/workbook-id.gen';
import type { WorkbookNameAndHash } from 'cadenza/repository/model/workbook-name-and-hash';
import { workbookNameAndHashString } from 'cadenza/repository/model/workbook-name-and-hash';
import type { RelationRoleGroupDto, RelationRoleGroupListDto } from 'cadenza/workbook/api/relation-role-group-dto';
import type { WorksheetId } from 'cadenza/workbook/api/worksheet-id';
import { parseAttributeId } from 'cadenza/workbook/model/attribute-id-util';
import type { AttributeId } from 'cadenza/workbook/model/attribute-id.gen';

export function copyObjecttypeWithData(id: ObjecttypeIdString, dto: CopyObjecttypeDto) {
  return ajax<WorkOrderId>(`/copy-objecttype-work-orders?sourceObjecttype=${btoa(id)}`, {
    type: 'POST',
    data: JSON.stringify(dto),
    contentType: MIMETYPE_JSON,
  });
}

export function updateObjecttypeWithData(id: ObjecttypeIdString, dto: UpdateObjecttypeDto) {
  return ajax<WorkOrderId>(`/objecttypes/${btoa(id)}/update-data-work-orders?updateData=true`, {
    type: 'POST',
    data: JSON.stringify(dto),
    contentType: MIMETYPE_JSON,
  });
}
export function getObjecttypeCopyContext(
  sourceObjecttypeId: ObjecttypeIdString,
  workbookNameAndHash: WorkbookNameAndHash,
  worksheetId: WorksheetId,
  selectedDataScope?: ObjecttypeDataScope,
) {
  return ajax<ObjecttypeCopyContextDto>(`/objecttypes/${btoa(sourceObjecttypeId)}/copy-objecttype-context`, {
    type: 'POST',
    contentType: MIMETYPE_JSON,
    data: JSON.stringify({
      workbookNameAndHash,
      worksheetId,
      selectedDataScope,
    }),
  });
}

export function analyzeObjecttypeBeforeUpdate(id: ObjecttypeIdString, workbookNameAndHash: WorkbookNameAndHash) {
  return ajax<WorkOrderId>(`/objecttypes/${btoa(id)}/update-data-validation-work-orders`, {
    type: 'POST',
    contentType: MIMETYPE_JSON,
    data: JSON.stringify({ workbookNameAndHash }),
  });
}

export function getChangesBetweenSourceAndCopiedObjecttypes(copiedObjecttypeId: ObjecttypeIdString) {
  return ajax<ChangesBetweenSourceAndCopiedObjecttypeDto>(
    `/objecttypes/${btoa(copiedObjecttypeId)}/changes-with-source-objecttype`,
    {
      type: 'GET',
      contentType: MIMETYPE_JSON,
    },
  );
}

/**
 * Checks whether object type is used in any workbook
 *
 * @param objecttypeId - {ObjecttypeId} - Object type id.
 * @return - Is object type shared with some workbook
 */
export function checkWhetherObjecttypeIsUsedInAnyWorkbook(objecttypeId: ObjecttypeIdString) {
  return ajax('/objecttypes/' + btoa(objecttypeId) + '/used-in-workbooks');
}

export function getObjecttypeAssociationCandidates(id: ObjecttypeIdString) {
  return ajax<AssociationCandidatesDto>(`/objecttypes/${btoa(id)}/associationCandidates`, { dataType: 'json' });
}

export function getWorkbookObjecttypeAssociationCandidates(
  workbookNameAndHash: WorkbookNameAndHash,
  objecttypeId: ObjecttypeIdString,
) {
  return ajax<AssociationCandidatesDto>(
    `/workbooks/${workbookNameAndHashString(workbookNameAndHash)}/objecttypes/${btoa(objecttypeId)}/association-candidates`,
    { dataType: 'json' },
  );
}

export function createObjecttypeAssociation(id: ObjecttypeIdString, associationInfo: MutableAssociationDto) {
  const url = `/objecttypes/${btoa(id)}/associations`;
  return ajax(url, {
    type: 'POST',
    data: JSON.stringify(associationInfo),
    contentType: MIMETYPE_JSON,
  });
}

/**
 * Retrieves existing association between requested objecttypes.
 *
 * @param firstObjecttypeId - The id of the BOT determining one end of the association
 * @param secondObjecttypeId - The id of the BOT determining the other end of the association
 * @return List of associations.
 */
export function getObjecttypeAssociation(
  firstObjecttypeId: ObjecttypeIdString,
  secondObjecttypeId: ObjecttypeIdString,
) {
  return ajax<AssociationDto>(
    `/objecttypes/${window.btoa(firstObjecttypeId)}/associations/${window.btoa(secondObjecttypeId)}`,
    {
      type: 'GET',
    },
  );
}

/**
 * Retrieves associations for requested objecttype.
 *
 * @param objecttypeId - Id of the objecttype for which attributes are requested.
 * @return List of associations.
 */
export function getObjecttypeAssociations(objecttypeId: ObjecttypeIdString) {
  return ajax<AssociationsDto>(`/objecttypes/${window.btoa(objecttypeId)}/associations`, { type: 'GET' });
}

/**
 * Updates association between requested objecttypes.
 *
 * @param associationInfo - The association to be updated
 * @return empty fulfilled promise
 */
export function updateObjecttypeAssociation(associationInfo: MutableAssociationDto) {
  const firstObjectTypeId = associationInfo.first.objecttypeId;
  const secondObjectTypeId = associationInfo.second.objecttypeId;
  return ajax<void>(`/objecttypes/${window.btoa(firstObjectTypeId)}/associations/${window.btoa(secondObjectTypeId)}`, {
    type: 'PUT',
    contentType: MIMETYPE_JSON,
    data: JSON.stringify(associationInfo),
  });
}

/**
 * Deletes association for requested objecttype.
 *
 * @param first - Id of the first objecttype
 * @param second - Id of the second objecttype
 * @return empty fulfilled promise
 */
export function deleteObjecttypeAssociation(first: ObjecttypeIdString, second: ObjecttypeIdString) {
  return ajax<void>(`/objecttypes/${window.btoa(first)}/associations/${window.btoa(second)}`, {
    type: 'DELETE',
  });
}

export function getVisibleObjecttypes(params: TableParams = {}) {
  return getObjecttypes({ privilege: 'visible', ...params });
}

/* Endpoint defaults to listing privilege. See net.disy.cadenza.repository.web.rest.ObjecttypesEndpointController.getObjecttypes  */
export function getObjecttypes({ privilege, ...params }: TableParams & { privilege?: ObjectPrivilege }) {
  const urlSearchParams = tableParams(params);
  if (privilege) {
    urlSearchParams.set('privilege', privilege);
  }
  return ajax<TableData<ObjecttypeListingDto>>(`/objecttypes?${urlSearchParams}`, { dataType: 'json' });
}

export function getNextHopObjecttypes(targetObjecttypeId: ObjecttypeIdString, path: ObjecttypeIdString[]) {
  const params = new URLSearchParams();

  for (const id of path) {
    params.append('path', window.btoa(id));
  }

  const url = `/objecttypes/${window.btoa(targetObjecttypeId)}/next-hop?${params.toString()}`;
  return ajax<ObjecttypePathElementDto[]>(url, { dataType: 'json' });
}

export function getObjecttypeUsages(objecttypeIds: ObjecttypeIdString[]): Promise<ObjecttypeUsagesDto>;
export function getObjecttypeUsages(
  objecttypeId: ObjecttypeIdString,
  params: TableParams,
): Promise<ObjecttypeUsagesDto>;
export function getObjecttypeUsages(objecttypeId: ObjecttypeIdString | ObjecttypeIdString[], params?: TableParams) {
  if (Array.isArray(objecttypeId)) {
    const searchParams = new URLSearchParams();
    searchParams.set('objecttypeIds', objecttypeId.map(btoa).join(','));
    return ajax<ObjecttypeUsagesDto>(`/objecttype-usages?${searchParams}`, { dataType: 'json' });
  } else {
    return ajax<ObjecttypeUsagesDto>(`/objecttypes/${window.btoa(objecttypeId)}/usages?${tableParams(params ?? {})}`, {
      dataType: 'json',
    });
  }
}

export function getVisibleObjecttypesWithMembers(repositoryName: RepositoryName) {
  return ajax<ObjecttypeDto[]>(`/repositories/${repositoryName}/objecttypes`, { dataType: 'json' });
}

/** @see net/disy/cadenza/repository/web/dto/TargetObjecttypeWithMembersDto.java */
interface TargetObjecttypeWithMembersDto {
  objecttype: ObjecttypeDto;
  objecttypePaths: ObjecttypePathDto[];
  objecttypeMembers: ObjecttypeMemberDto[];
}

/**
 * Gets objecttypes that are reachable from the given one via attributes.
 *
 * @param sourceObjecttypeId the Objecttype to start analysis from
 * @param includeMembers - whether to include member dtos in the response
 * @return A list of connected objecttypes and their members
 */
export function getConnectedObjecttypes(sourceObjecttypeId: ObjecttypeIdString, includeMembers: boolean) {
  const params = new URLSearchParams({ includeMembers: String(includeMembers) });
  return ajax<TargetObjecttypeWithMembersDto[]>(
    `/objecttypes/${window.btoa(sourceObjecttypeId)}/connected-objecttypes?${params}`,
    { dataType: 'json' },
  );
}

/**
 * Updates an object type, only changed fields are written.
 *
 * @param id - The ID of the OT to update
 * @param patch - Sparse details (only id and changed fields included)
 * @return Resolves with true when object type is updated.
 */
export function updateObjecttype(id: ObjecttypeIdString, patch: Partial<MutableObjecttypeDto>) {
  return ajax<ObjecttypeDto>(`/objecttypes/${window.btoa(id)}`, createPatch(patch));
}

/**
 * Deletes multiple objecttypes.
 *
 * @param objecttypeIds - Ids of all the objecttypes that should be deleted
 * @return Resolves when all of the requested objecttypes are deleted. Rejects otherwise.
 */
export function deleteObjecttypes(objecttypeIds: ObjecttypeId[]) {
  return ajax<void>('/objecttypes', {
    method: 'DELETE',
    data: JSON.stringify({ objecttypeIds }),
    contentType: MIMETYPE_JSON,
  });
}

export function createBasicObjecttypes(createObjecttypeRequest: CreateBasicObjecttypeRequestDto[]) {
  return ajax<ObjecttypeDto[]>('/objecttypes?type=basic', {
    method: 'POST',
    data: JSON.stringify(createObjecttypeRequest),
    contentType: MIMETYPE_JSON,
  });
}

export function createCombinedObjecttype(createObjecttypeRequest: CreateCombinedObjecttypeRequestDto) {
  return ajax<ObjecttypeDto>('/objecttypes?type=combined', {
    method: 'POST',
    data: JSON.stringify(createObjecttypeRequest),
    contentType: MIMETYPE_JSON,
  });
}

export function addMembersToCombinedObjecttype(
  targetObjectTypeId: ObjecttypeIdString,
  additionalMembers: ObjecttypeSelectedMembersDto[],
) {
  return ajax<ObjecttypeDto>(`/objecttypes/${window.btoa(targetObjectTypeId)}/members?type=combined`, {
    method: 'POST',
    data: JSON.stringify(additionalMembers),
    contentType: MIMETYPE_JSON,
  });
}

export function updateCombinedObjecttypePaths(
  targetObjectTypeId: ObjecttypeIdString,
  pathsToUpdate: ObjecttypePathDto[],
) {
  return ajax<ObjecttypeDto>(`/objecttypes/${window.btoa(targetObjectTypeId)}/objecttype-paths?type=combined`, {
    method: 'PUT',
    data: JSON.stringify(pathsToUpdate),
    contentType: MIMETYPE_JSON,
  });
}

export function duplicateObjecttype(sourceObjecttype: ObjecttypeIdString) {
  return ajax<ObjecttypeDto>(`/objecttypes?sourceObjecttype=${window.btoa(sourceObjecttype)}`, { method: 'POST' });
}

export function setEnrichmentGrouping(objecttypeId: ObjecttypeIdString, grouped: boolean) {
  return ajax<void>(`/objecttypes/${window.btoa(objecttypeId)}/enrichment/grouped`, {
    method: 'PUT',
    contentType: MIMETYPE_JSON,
    data: Boolean(grouped).toString(),
  });
}

export function convertEnrichmentToStandardObjecttype(objecttypeId: ObjecttypeIdString) {
  return ajax<void>(`/objecttypes/${window.btoa(objecttypeId)}/enrichment`, {
    method: 'DELETE',
  });
}

/**
 *
 * Gets date hierarchy
 *
 * @param objecttypeId - Objecttype id
 * @param hierarchyId - Hierarchy id
 * @return DateHierarchyDto - Hierarchy details
 */
export function getDateHierarchy(objecttypeId: ObjecttypeIdString, hierarchyId: string) {
  return ajax<DateHierarchyDto>(
    `/objecttypes/${window.btoa(objecttypeId)}/date-hierarchies/${encodePathVariable(hierarchyId)}`,
    {
      method: 'GET',
      contentType: MIMETYPE_JSON,
    },
  );
}

/**
 *
 * Updates date hierarchy
 *
 * @param objecttypeId - Objecttype id
 * @param hierarchyId - Hierarchy id
 * @param details - Object with objecttypeId, id and property to be changed
 * @return
 */
export function updateDateHierarchy(
  objecttypeId: ObjecttypeIdString,
  hierarchyId: string,
  details: Partial<DateHierarchyDto>,
) {
  return ajax<DateHierarchyDto>(
    `/objecttypes/${window.btoa(objecttypeId)}/date-hierarchies/${encodePathVariable(hierarchyId)}`,
    {
      type: 'PATCH',
      data: JSON.stringify(details),
      contentType: MIMETYPE_JSON,
    },
  );
}

/**
 * Creates new time hierarchy
 *
 * @param objecttypeId - Objecttype.id
 * @param timeHierarchy - Object with hierarchy data
 * @param timeHierarchy.objecttype - Objecttype
 * @param timeHierarchy.attributeName - Attribute Name
 * @param timeHierarchy.hierarchyLevels - Hierarchy levels
 * @return Resolves when new time hierarchy was created and persisted
 */
export function createTimeHierarchy(objecttypeId: ObjecttypeIdString, timeHierarchy: Partial<DateHierarchyDto>) {
  return ajax<void>(`/objecttypes/${window.btoa(objecttypeId)}/date-hierarchies`, {
    method: 'POST',
    data: JSON.stringify(timeHierarchy),
    contentType: MIMETYPE_JSON,
  });
}

/**
 * Creates new multi attribute hierarchy
 *
 * @param objecttypeId
 * @param attributeHierarchy
 * @return Resolves when new dimension was created and persisted
 */
export function createMultiAttributeHierarchy(
  objecttypeId: ObjecttypeIdString,
  attributeHierarchy: Partial<MultiAttributeHierarchyDto>,
) {
  return ajax<void>(`/objecttypes/${window.btoa(objecttypeId)}/multi-attribute-hierarchies`, {
    method: 'POST',
    data: JSON.stringify(attributeHierarchy),
    contentType: MIMETYPE_JSON,
  });
}

/**
 * Details of the multi attribute hierarchy
 *
 * @param objecttypeId
 * @param hierarchyName
 * @return Resolves when new dimension was created and persisted
 */
export function getMultiAttributeHierarchy(objecttypeId: ObjecttypeIdString, hierarchyName: string) {
  return ajax<MultiAttributeHierarchyDto>(
    `/objecttypes/${window.btoa(objecttypeId)}/multi-attribute-hierarchies/${encodePathVariable(hierarchyName)}`,
  );
}

/**
 * Dissolves multi attribute hierarchy to its single attributes
 *
 * @param objecttypeId
 * @param hierarchyName
 * @return Resolves when the multi attribute hierarchy is dissolved.
 */
export function dissolveMultiAttributeHierarchy(objecttypeId: ObjecttypeIdString, hierarchyName: string) {
  return ajax<void>(
    `/objecttypes/${window.btoa(objecttypeId)}/multi-attribute-hierarchies/${encodePathVariable(hierarchyName)}`,
    {
      type: 'DELETE',
    },
  );
}

/**
 * Updates given multi attribute hierarchy, only changed fields are written.
 *
 * @param objecttypeId
 * @param hierarchyName
 * @param hierarchy - hierarchies to be updated
 * @return Resolves when attribute is updated.
 */
export function updateMultiAttributeHierarchy(
  objecttypeId: ObjecttypeIdString,
  hierarchyName: string,
  hierarchy: Partial<MultiAttributeHierarchyDto>,
) {
  return ajax<MultiAttributeHierarchyDto>(
    `/objecttypes/${window.btoa(objecttypeId)}/multi-attribute-hierarchies/${encodePathVariable(hierarchyName)}`,
    {
      type: 'PATCH',
      data: JSON.stringify(hierarchy),
      contentType: MIMETYPE_JSON,
    },
  );
}

/**
 * Retrieves details for requested objecttype.
 *
 * @param objecttypeId - Id of the objecttype for which details are requested.
 * @param includeMembers - whether to include member dtos in the response
 * @return Details of the objecttype.
 */
export function getObjecttypeDetails(objecttypeId: ObjecttypeIdString, includeMembers = false) {
  const params = new URLSearchParams({ includeMembers: String(includeMembers) });
  return ajax<ObjecttypeDto>(`/objecttypes/${window.btoa(objecttypeId)}?${params}`, { dataType: 'json' });
}

/**
 * Does a quick HEAD request to see if the objecttype exists and is accessible.
 *
 * @param objecttypeId - self-explanatory
 */
export async function isObjecttypeAvailableAndUnbound(objecttypeId: ObjecttypeIdString): Promise<boolean> {
  try {
    return !(await ajax<WorkbookId>(`/objecttypes/${window.btoa(objecttypeId)}/parent-workbook`));
  } catch {
    return false; // error ignored in this case
  }
}

/**
 * Retrieves objecttype member with given name for requested objecttype.
 *
 * @param objecttypeId - Id of the objecttype for which attributes are requested.
 * @param memberId - Id of the requested objecttype member
 * @param attributeFilter - attribute filtering criteria
 * @return List of objecttype members.
 */
export function getObjecttypeMember(
  objecttypeId: ObjecttypeIdString,
  memberId: string,
  attributeFilter: AttributeFilter = 'ALL',
) {
  const urlParams = new URLSearchParams({ attributeFilter });
  return ajax<ObjecttypeMemberDto>(
    `/objecttypes/${window.btoa(objecttypeId)}/members/${encodePathVariable(memberId)}?${urlParams}`,
    { dataType: 'json' },
  );
}

/**
 * Retrieves objecttype members for requested objecttype.
 *
 * @param objecttypeId - Id of the objecttype for which attributes are requested.
 * @param attributeFilter - attribute filtering criteria
 * @param signal - abort signal
 * @return List of objecttype members.
 */
export function getObjecttypeMembers(
  objecttypeId: ObjecttypeIdString,
  attributeFilter: AttributeFilter = 'ALL',
  signal?: AbortSignal,
) {
  const urlParams = new URLSearchParams({ attributeFilter });
  return ajax<ObjecttypeMemberDto[]>(`/objecttypes/${window.btoa(objecttypeId)}/members?${urlParams}`, {
    dataType: 'json',
    signal,
  });
}

type AttributePatch = Pick<AttributeDto, 'objecttypeId' | 'name'> & Partial<AttributeDto>;

/**
 * Updates given attribute of objecttype, only changed fields are written.
 *
 * @param details - Sparse details (only id and changed fields included)
 * @return Resolves when attribute is updated.
 */
export function updateAttribute(details: AttributePatch) {
  return ajax<AttributeDto>(
    `/objecttypes/${window.btoa(details.objecttypeId)}/attributes/${encodePathVariable(details.name)}`,
    createPatch(details),
  );
}

/**
 * Reposition the specified members so that they immediately precede the given next member in the list.
 * The specified members will become direct neighbors in the same order they are provided.
 * The next member serves as a reference point and remains in its original position.
 *
 * @param objecttypeId - ID of the objecttype the members belong to
 * @param memberNames - The IDs of the objecttype member for which the order has to be changed in target order
 * @param nextMemberName - The IDs of the member before which the members should be inserted. If undefined the member is
 *   moved to the end of the member list.
 * @return Resolves when objecttype member was sorted
 */
export function reorderObjecttypeMembers(
  objecttypeId: ObjecttypeIdString,
  memberNames: string[],
  nextMemberName: string | undefined,
) {
  return ajax<void>(`/objecttypes/${window.btoa(objecttypeId)}/members`, {
    method: 'PATCH',
    data: JSON.stringify({ memberNames, nextMemberName }),
    contentType: MIMETYPE_JSON,
  });
}

/**
 * Duplicate Objecttype members
 *
 * @param objecttypeId the Objecttype containing the members to duplicate
 * @param sourceMemberNames the Objecttype-local names of the members to duplicate
 * @return the Objecttype-local names of the newly-duplicated members
 */
export function duplicateObjecttypeMembers(objecttypeId: ObjecttypeIdString, sourceMemberNames: string[]) {
  // objecttypeId already contains a slash that separates repositoryName and objecttypeName
  return ajax<string[]>(`/objecttypes/${objecttypeId}/members`, {
    method: 'POST',
    data: JSON.stringify(sourceMemberNames),
    contentType: MIMETYPE_JSON,
  });
}

/**
 * Deletes objecttype members
 *
 * @param objecttypeId
 * @param membersIds
 * @return Resolves when new dimension group was deleted and persisted
 */
export function deleteObjecttypeMembers(objecttypeId: ObjecttypeIdString, membersIds: string[]) {
  return ajax<void>(`/objecttypes/${window.btoa(objecttypeId)}/members`, {
    method: 'DELETE',
    data: JSON.stringify(membersIds),
    contentType: MIMETYPE_JSON,
  });
}

/**
 * Reset overridden objecttype member properties for given reference member.
 *
 * @param objecttypeId
 * @param memberId
 * @param properties
 * @return Resolves when objecttype member properties were reset.
 */
export function resetObjecttypeMemberProperties(
  objecttypeId: ObjecttypeIdString,
  memberId: string,
  properties: OverrideableObjecttypeMemberProperty[],
) {
  return ajax<ObjecttypeMemberDto>(
    `/objecttypes/${window.btoa(objecttypeId)}/members/${encodePathVariable(memberId)}`,
    {
      method: 'PATCH',
      data: JSON.stringify(properties),
      contentType: MIMETYPE_JSON,
    },
  );
}

/**
 * Gets all datasource table columns for the given objecttype.
 *
 * @param objecttypeId - id of objecttype from which to get the columns
 * @return Array of all table columns together with usage information. Throws an exception if objecttype is of type combined and therefore has no table columns.
 */
export function getDatatableColumns(objecttypeId: ObjecttypeIdString) {
  return ajax<DatatableColumnDto[]>(`/objecttypes/${window.btoa(objecttypeId)}/datatable-columns`);
}

/**
 * Create new attributes for the given objecttype.
 *
 * @param objecttypeId - id for the (basic) objecttype for which new attributes should be created
 * @param columnNames - names of the table columns for the new attributes
 * @return Resolves when attributes could be created
 */
export function createAttributes(objecttypeId: ObjecttypeIdString, columnNames: string[]) {
  return ajax<void>(`/objecttypes/${window.btoa(objecttypeId)}/attributes?type=basic`, {
    method: 'POST',
    data: JSON.stringify({ columnNames }),
    contentType: MIMETYPE_JSON,
  });
}

/**
 * Create new attribute with database column for the given basic objecttype.
 * The basic objecttype must be bound to workbook.
 *
 * @param objecttypeId - id for the (basic, bound to workbook) objecttype for which new attributes should be created
 * @param attribute - attribute properties
 * @return Resolves when attribute could be created
 */
export function createAttributeAndDatabaseColumn(
  objecttypeId: ObjecttypeIdString,
  attribute: CreateAttributeRequestDto,
) {
  return ajax<void>(`/objecttypes/${window.btoa(objecttypeId)}/attribute?type=basicBoundToWorkbook`, {
    method: 'PUT',
    data: JSON.stringify(attribute),
    contentType: MIMETYPE_JSON,
  });
}

export function getRelationRoleGroups(objecttypeId: ObjecttypeIdString) {
  return ajax<RelationRoleGroupDto[]>(`/objecttypes/${window.btoa(objecttypeId)}/relations`, {
    method: 'GET',
  });
}

export function getRelationRoleGroupsOfAttribute(objecttypeId: ObjecttypeIdString, attributeName: string) {
  return ajax<RelationRoleGroupDto[]>(
    `/objecttypes/${window.btoa(objecttypeId)}/attributes/${encodePathVariable(attributeName)}/relations`,
    {
      method: 'GET',
    },
  );
}

export function putRelationRoleGroups(objecttypeId: ObjecttypeIdString, relationGroupList: RelationRoleGroupListDto) {
  return ajax<void>(`/objecttypes/${window.btoa(objecttypeId)}/relations`, {
    method: 'PUT',
    data: JSON.stringify(relationGroupList),
    contentType: MIMETYPE_JSON,
  });
}

export function testExistingFunctionFormula(attributeName: string, objecttypeId: ObjecttypeIdString) {
  return ajax<FunctionFormulaStatusDto>(
    `/objecttypes/${window.btoa(objecttypeId)}/function-attributes/${encodePathVariable(attributeName)}/status`,
    {
      dataType: 'json',
    },
  );
}

export function getAttributeValues(objecttypeId: ObjecttypeIdString, attributeName: string, filterText = '') {
  const baseUrl = `/objecttypes/${window.btoa(objecttypeId)}/attributes/${encodePathVariable(attributeName)}/table?`;
  const url = baseUrl + new URLSearchParams({ filterText });
  return ajax<TableData<AttributeValueRowDto>>(url, { dataType: 'json' });
}

export function updateAttributeColorRules(
  objecttypeId: ObjecttypeIdString,
  newRules: { colorRules: AttributeValueColorRuleDtoWithOptionalColor[] },
) {
  return ajax<void>(`/objecttypes/${window.btoa(objecttypeId)}/attribute-color-rules`, {
    method: 'PUT',
    data: JSON.stringify(newRules),
    contentType: MIMETYPE_JSON,
  });
}

export function clearAttributeColorRules(objecttypeId: ObjecttypeIdString, attributeName: string) {
  return ajax<void>(
    `/objecttypes/${window.btoa(objecttypeId)}/attributes/${encodePathVariable(attributeName)}/attribute-color-rules`,
    {
      method: 'DELETE',
    },
  );
}

export function invalidateCachesForObjecttypes(objecttypeIds: ObjecttypeId[]) {
  return ajax<void>('/cache', {
    method: 'DELETE',
    data: JSON.stringify({ objecttypeIds }),
    contentType: MIMETYPE_JSON,
  });
}

export function getDefaultColorClasses(attributeId: AttributeId) {
  const [objecttypeIdString, attributeName] = parseAttributeId(attributeId);
  const url = `/objecttypes/${window.btoa(objecttypeIdString)}/attributes/${encodePathVariable(attributeName)}/color-rules`;
  return ajax<AttributeColorRulesListDto>(url, { dataType: 'json' });
}
